import {AfterViewInit, Component, OnInit} from '@angular/core';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {Observable, throwError} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';
import {MatTableDataSource} from '@angular/material/table';
import {DataService, DatasetBannerModel, DatasetBannerView} from 'src/app/services/data.service';
import {SessionService, UserType} from 'src/app/services/session.service';
import {TrustiiService} from 'src/app/services/trustii.service';
import {_} from 'underscore';
import {DialogService} from 'src/app/services/dialog.service';
import {saveAs} from 'file-saver';

@Component({
  selector: 'app-leaderboard-v2',
  templateUrl: './leaderboard-v2.component.html',
  styleUrls: ['./leaderboard-v2.component.scss'],
})
export class LeaderboardV2Component implements OnInit, AfterViewInit {
  private params: Params;
  enterpriseUser = null;
  isReady = false;
  selectedmetrix = null;
  modalList = [];
  datasetDisplayName = '';
  classificationProblem = true;
  scope = 'publicScore';
  dataset: any = null;
  canShowPrivateLeaderboard = false;
  isChallengeEnded = false;
  public dataSource: MatTableDataSource<any> = new MatTableDataSource([]);
  displayedColumns = [
    'rank',
    'members',
    'name',
    'publicScore',
    'entries',
    'best',
    'last',
  ];
  currentUser = null;
  team = {
    id: 0,
    name: '',
  };

  constructor(
    private sessionService: SessionService,
    private datasetBannerModel: DatasetBannerModel,
    private route: ActivatedRoute,
    private trustiiService: TrustiiService,
    private dialogService: DialogService,
    public dataservice: DataService
  ) {
    this.dataSource = new MatTableDataSource([]);
  }

  ngOnInit() {
    this.currentUser = this.sessionService.getUser();
    if (this.dataservice.data.accountId === this.sessionService.getUser().roleId) {
      this.enterpriseUser = true;
    } else {
      this.enterpriseUser = false;
    }
    this.datasetBannerModel.setBannerView(DatasetBannerView.LeaderboardView);
  }

  /** _loadParams$ method
   *
   * Load params and set class properties.
   * @return Observable<Params>
   */
  private _loadParams$(): Observable<Params> {
    return this.route.params.pipe(
      map((params: Params) => (this.params = params))
    );
  }

  _loadLeaderBoard$() {
    this.modalList = [];
    this.isReady = false;
    this.trustiiService
      .getLeaderboard(
        this.params.datasetId,
        1,
        10000,
        this.scope === 'publicScore'
          ? 'defaultPublicScore'
          : 'defaultPrivateScore'
      )
      .subscribe(
        (res) => {
          this._loadBaseline$(res);
          if (!this.enterpriseUser) {
            this._loadVendorTeam$();
          }
        },
        (err) => {
          this.isReady = true;
        }
      );
  }

  _loadVendorTeam$() {
    this.trustiiService.getVendorDatasetTeam(this.params.datasetId).subscribe(
      (res) => {
        this.team = res.data.team;
      },
      (err) => {
        this.isReady = true;
      }
    );
  }

  /**
   * List all the vendor models for specific datasetId
   */
  _loadBaseline$(predictions: any) {
    this.trustiiService.getBaseline(this.params.datasetId).subscribe(
      (res) => {
        this.processResponse(predictions, res);
      },
      (err) => {
        this.isReady = true;
      }
    );
  }

  private _loadDataset$(): void {
    this.trustiiService
      .getDataset(this.params.datasetId)
      .pipe(
        tap((dataset: any) => (this.dataset = dataset.data)),
        tap(() => {
          if (this.dataset.resultDate) {
            if (this.dataservice.isEndDateExpired(this.dataset.resultDate)) {
              this.canShowPrivateLeaderboard = true;
            }
          } else if (!this.dataset.endDate || (this.dataset.endDate && this.dataservice.isEndDateExpired(this.dataset.endDate))) {
            this.canShowPrivateLeaderboard = true;
          }

          if (this.dataset.endDate && this.dataservice.isEndDateExpired(this.dataset.endDate)) {
            this.isChallengeEnded = true;
          }
        }),
        tap(this._loadLeaderBoard$.bind(this)),
        catchError((error) => {
          return throwError(error);
        })
      )
      .subscribe();
  }

  ngAfterViewInit() {
    this._loadParams$()
      .pipe(tap(this._loadDataset$.bind(this)))
      .subscribe();

    if (!this.enterpriseUser) {
      this.datasetBannerModel.setBannerView(DatasetBannerView.LeaderboardView);
    }
  }

  processResponse(res, modelRes) {
    if (this.dataset && this.dataset.mlProblem === 'regression') {
      this.classificationProblem = false;
      this.selectedmetrix = this.dataset.targetScore
        ? this.dataset.targetScore
        : 'r2_score';
      this.datasetDisplayName = this.dataset.displayName;
    } else if (
      this.dataset &&
      (this.dataset.mlProblem === 'classification' ||
        this.dataset.mlProblem === 'multi-classification' ||
        this.dataset.mlProblem === 'multi-labels-classification')
    ) {
      this.classificationProblem = true;
      this.selectedmetrix = this.dataset.targetScore
        ? this.dataset.targetScore
        : 'accuracy_score';
      this.datasetDisplayName = this.dataset.displayName;
    }

    this.modalList = res.data;
    if (
      this.modalList &&
      this.modalList.length > 0 &&
      (this.modalList[0].mlProblem === 'classification' ||
        this.modalList[0].mlProblem === 'multi-classification' ||
        this.modalList[0].mlProblem === 'multi-labels-classification')
    ) {
      this.modalList.forEach((model) => {
        if (model['publicScore'] && model['publicScore'].confusion_matrix) {
          Object.assign(
            model['publicScore'],
            this.generateConfusionMatrixReport(
              model['publicScore'].confusion_matrix
            )
          );
        }

        if (model['privateScore'] && model['privateScore'].confusion_matrix) {
          Object.assign(
            model['privateScore'],
            this.generateConfusionMatrixReport(
              model['privateScore'].confusion_matrix
            )
          );
        }
      });
    }
    this.modalList = this.modalList.filter(
      (model) => model.datasetId === parseInt(this.params.datasetId, 10)
    );
    const latestAutoModel = [];
    if (this.dataset.useCustomBaseline === 'yes') {
      const baseline = {
        publicScore: {},
        privateScore: {},
        score: this.dataset.customBaseline,
        displayName: 'Baseline',
        type: 'baseline',
      };
      baseline['publicScore'][this.selectedmetrix] =
        this.dataset.customBaseline;
      baseline['privateScore'][this.selectedmetrix] =
        this.dataset.customBaseline;
      latestAutoModel.push(baseline);
    } else {
      if (modelRes.data.id && modelRes.data.id > 0) {
        latestAutoModel.push(modelRes.data);
      }
      if (latestAutoModel.length > 0) {
        latestAutoModel[0].displayName = 'Auto ML Model';
        latestAutoModel[0].publicScore = latestAutoModel[0].publicScore
          ? latestAutoModel[0].publicScore
          : {};
        latestAutoModel[0].privateScore = latestAutoModel[0].privateScore
          ? latestAutoModel[0].privateScore
          : {};
        if (
          latestAutoModel[0].publicScore &&
          latestAutoModel[0].privateScore &&
          latestAutoModel[0].accuracyDetails.confusion_matrix
        ) {
          Object.assign(
            latestAutoModel[0].publicScore,
            this.generateConfusionMatrixReport(
              latestAutoModel[0].accuracyDetails.confusion_matrix
            )
          );
          Object.assign(
            latestAutoModel[0].privateScore,
            this.generateConfusionMatrixReport(
              latestAutoModel[0].accuracyDetails.confusion_matrix
            )
          );
        }
      }
    }
    this.modalList = [...latestAutoModel, ...this.modalList];
    this.modalList.forEach((value: any, index: number) => {
      this.modalList[index].downloading = false;
    });
    this.sortById(this.selectedmetrix);
    this.isReady = true;
  }

  sortById(id) {
    const data = _.sortBy(
      this.modalList,
      (item) => -item[this.scope][this.selectedmetrix]
    );
    const users = [];
    const finalData = [];
    data.forEach((value) => {
      if (!users.includes(value.creatorId)) {
        users.push(value.creatorId);
        finalData.push(value);
      }
    });
    this.dataSource.data = finalData;
  }

  updateScope(scope): void {
    this.scope = scope;
    if (
      scope === 'privateScore' &&
      (this.dataset.canDownloadNotebook === 'public' || (this.enterpriseUser && this.dataset.canDownloadNotebook === 'private'))
    ) {
      this.displayedColumns = [
        'rank',
        'members',
        'name',
        'publicScore',
        'entries',
        'best',
        'last',
        'notebooks'
      ];
    } else {
      this.displayedColumns = [
        'rank',
        'members',
        'name',
        'publicScore',
        'entries',
        'best',
        'last',
      ];
    }
    this._loadLeaderBoard$();
  }

  getTeamMembers(teamId: number, teamName: string) {
    this.trustiiService
      .getTeamMembers(this.dataset.id, teamId)
      .pipe(
        tap((members: any) => {
          let text = '<div class="flex align-items-center">';
          members.data.forEach((member) => {
            text += member.firstname + ' ' + member.lastname + '<br/>';
          });
          text += '</div>';
          this.dialogService.openAlertDialog(
            '350px',
            text,
            teamName + ' members',
            'Ok',
            'modal-informative'
          );
        }),
        catchError((error) => {
          return throwError(error);
        })
      )
      .subscribe();
  }

  /**
   *
   * The false positive rate is calculated as FP/FP+TN, where FP is the number of false positives and TN is the
   *  number of true negatives (FP+TN being the total number of negatives). It’s the probability that a false
   * alarm will be raised: that a positive result will be given when the true value is negative.
   * There are many other possible measures of test accuracy and error rate. Here is a short rundown of the most common ones:
   * The false negative rate – also called the miss rate – is the probability that a true positive will be misses
   * by the test. It’s calculated as FN/FN+TP, where FN is the number of false negatives and TP is the number of
   * true positives (FN+TP being the total number of positives).
   * The true positive rate (TPR, also called sensitivity) is calculated as TP/TP+FN. TPR is
   * the probability that an actual positive will test positive.
   * The true negative rate (also called specificity), which is the probability that an actual
   * negative will test negative. It is calculated as TN/TN+FP.
   *
   * from sklearn.metrics import confusion_matrix
   * y_true = [0,0,0,1,0,0,1,0,0,1,0,1,0,0,0,0,1,0,0,1,1,0,1,0,0,0,0]
   * y_pred = [0,0,0,1,0,0,1,0,0,1,0,1,0,0,0,0,1,0,0,0,0,1,0,1,0,0,0]
   * confusion_matrix(y_pred, y_true)
   * Input array :
   * array([[17,  3],
   * [ 2,  5]])
   *
   * TP [1][1]
   * FP [1][0]
   * TN [0][0]
   * FN [0][1]
   *
   * TPR  TP/TP+FN
   * FPR  FP/FP+TN
   * FNR  FN/FN+TP
   * TNR  TN/TN+FP
   *
   * table     pn  pp
   *        an 17  2
   *        ap 3   5
   */
  generateConfusionMatrixReport(confusionMatrix) {
    return {
      True_Positive_Rate:
        confusionMatrix[1][1] / (confusionMatrix[1][1] + confusionMatrix[0][1]),
      False_Positive_Rate:
        confusionMatrix[1][0] / (confusionMatrix[1][0] + confusionMatrix[0][0]),
      False_Negative_Rate:
        confusionMatrix[0][1] / (confusionMatrix[0][1] + confusionMatrix[1][1]),
      True_Negative_Rate:
        confusionMatrix[0][0] / (confusionMatrix[0][0] + confusionMatrix[1][0]),
    };
  }

  downloadNotebook(prediction: any) {
    window.open(this.trustiiService.getPredictionNotebookLink(prediction.id));
  }

  downloadingStatus(prediction: any, status: boolean) {
    this.modalList.forEach((value: any, index: number) => {
      if (value.id === prediction.id && !prediction.type) {
        this.modalList[index].downloading = status;
      }
    });
  }
}
